import { useEffect, useMemo, useState } from "react";
import { Dumbbell, Info, Languages, Plus, RotateCcw, Save, Search, Trash2 } from "lucide-react";
import { defaultPlan, languageLabels, loadExerciseData, preferredLanguage } from "./data";
import type { Exercise, LanguageCode, PlanItem, WorkoutDay, WorkoutPlan } from "./types";

const storageKey = "workout.plan.v1";

function readPlan(): WorkoutPlan {
  try {
    const value = localStorage.getItem(storageKey);
    if (!value) return defaultPlan;
    const parsed = JSON.parse(value) as WorkoutPlan;
    return parsed.version === 1 && Array.isArray(parsed.days) ? parsed : defaultPlan;
  } catch {
    return defaultPlan;
  }
}

function Media({ exercise }: { exercise: Exercise }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="mediaFallback" aria-label="Exercise media unavailable">
        <Dumbbell size={28} />
        <span>{exercise.category}</span>
      </div>
    );
  }
  return <img src={exercise.mediaGifUrl} alt={`${exercise.name} demonstration`} onError={() => setFailed(true)} />;
}

function Chip({
  value,
  selected,
  onClick
}: {
  value: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`chip ${selected ? "selected" : ""}`} onClick={onClick} type="button">
      {value}
    </button>
  );
}

function ExerciseCard({
  exercise,
  selected,
  onSelect,
  onAdd
}: {
  exercise: Exercise;
  selected: boolean;
  onSelect: () => void;
  onAdd: () => void;
}) {
  return (
    <article className={`exerciseCard ${selected ? "active" : ""}`}>
      <button className="cardButton" onClick={onSelect} type="button">
        <div className="cardMedia">
          <Media exercise={exercise} />
        </div>
        <div className="cardBody">
          <h3>{exercise.name}</h3>
          <div className="tags">
            <span className="tag blue">{exercise.category}</span>
            <span className="tag green">{exercise.equipment}</span>
          </div>
        </div>
      </button>
      <button className="iconButton addButton" onClick={onAdd} type="button" aria-label={`Add ${exercise.name} to plan`}>
        <Plus size={16} />
      </button>
    </article>
  );
}

function ExerciseDetail({
  exercise,
  language,
  languages,
  onLanguage,
  onAdd
}: {
  exercise: Exercise;
  language: LanguageCode;
  languages: LanguageCode[];
  onLanguage: (language: LanguageCode) => void;
  onAdd: () => void;
}) {
  return (
    <aside className="detailPane" aria-label="Exercise detail">
      <div className="detailMedia">
        <Media exercise={exercise} />
      </div>
      <div className="detailHeader">
        <h2>{exercise.name}</h2>
        <button className="primaryButton" type="button" onClick={onAdd}>
          <Plus size={16} /> Add
        </button>
      </div>
      <div className="metaGrid">
        <span>
          <strong>Equipment</strong>
          {exercise.equipment}
        </span>
        <span>
          <strong>Target</strong>
          {exercise.target}
        </span>
        <span>
          <strong>Body part</strong>
          {exercise.bodyPart}
        </span>
        <span>
          <strong>Group</strong>
          {exercise.muscleGroup}
        </span>
      </div>
      <section>
        <h3>Secondary muscles</h3>
        <div className="tags">
          {exercise.secondaryMuscles.map((muscle) => (
            <span className="tag" key={muscle}>
              {muscle}
            </span>
          ))}
        </div>
      </section>
      <section>
        <div className="sectionRow">
          <h3>Instructions</h3>
          <Languages size={16} />
        </div>
        <div className="languageTabs" role="tablist" aria-label="Instruction language">
          {languages.map((lang) => (
            <button
              className={language === lang ? "active" : ""}
              key={lang}
              onClick={() => onLanguage(lang)}
              type="button"
            >
              {languageLabels[lang]}
            </button>
          ))}
        </div>
        <ol className="steps">
          {exercise.instructionSteps[language].map((step, index) => (
            <li key={`${exercise.id}-${language}-${index}`}>{step}</li>
          ))}
        </ol>
      </section>
    </aside>
  );
}

function PlanBuilder({
  plan,
  selectedDay,
  onDay,
  onUpdate,
  exerciseById
}: {
  plan: WorkoutPlan;
  selectedDay: string;
  onDay: (day: string) => void;
  onUpdate: (plan: WorkoutPlan) => void;
  exerciseById: Map<string, Exercise>;
}) {
  const day = plan.days.find((item) => item.id === selectedDay) ?? plan.days[0];

  function updateDay(nextDay: WorkoutDay) {
    onUpdate({ ...plan, days: plan.days.map((item) => (item.id === nextDay.id ? nextDay : item)) });
  }

  function updateItem(item: PlanItem, patch: Partial<PlanItem>) {
    updateDay({ ...day, items: day.items.map((entry) => (entry.id === item.id ? { ...entry, ...patch } : entry)) });
  }

  function removeItem(id: string) {
    updateDay({ ...day, items: day.items.filter((entry) => entry.id !== id) });
  }

  return (
    <section className="planPane" aria-label="Workout plan builder">
      <div className="paneHeader">
        <div>
          <p className="eyebrow">Local plan</p>
          <h2>Workout Plan</h2>
        </div>
        <Save size={18} aria-label="Saved locally" />
      </div>
      <div className="dayTabs">
        {plan.days.map((item) => (
          <button className={item.id === selectedDay ? "active" : ""} key={item.id} onClick={() => onDay(item.id)} type="button">
            {item.name.slice(0, 3)}
            <span>{item.items.length}</span>
          </button>
        ))}
      </div>
      <div className="planList">
        {day.items.length === 0 ? (
          <div className="emptyState">
            <Dumbbell size={26} />
            <p>Add exercises from the library to build {day.name}.</p>
          </div>
        ) : (
          day.items.map((item) => {
            const exercise = exerciseById.get(item.exerciseId);
            return (
              <div className="planItem" key={item.id}>
                <div className="planTitle">
                  <strong>{exercise?.name ?? "Unknown exercise"}</strong>
                  <button className="iconButton" type="button" onClick={() => removeItem(item.id)} aria-label="Remove exercise">
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="planFields">
                  <label>
                    Sets
                    <input type="number" min="1" value={item.sets} onChange={(event) => updateItem(item, { sets: Number(event.target.value) })} />
                  </label>
                  <label>
                    Reps
                    <input type="number" min="1" value={item.reps} onChange={(event) => updateItem(item, { reps: Number(event.target.value) })} />
                  </label>
                  <label>
                    Weight
                    <input value={item.weight} onChange={(event) => updateItem(item, { weight: event.target.value })} placeholder="body" />
                  </label>
                  <label>
                    Rest
                    <input
                      type="number"
                      min="0"
                      value={item.restSeconds}
                      onChange={(event) => updateItem(item, { restSeconds: Number(event.target.value) })}
                    />
                  </label>
                </div>
                <textarea value={item.notes} onChange={(event) => updateItem(item, { notes: event.target.value })} placeholder="Notes" />
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

export function App() {
  const [data, setData] = useState<{ exercises: Exercise[]; taxonomy: Awaited<ReturnType<typeof loadExerciseData>>["taxonomy"] } | null>(null);
  const [dataError, setDataError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [equipment, setEquipment] = useState("");
  const [target, setTarget] = useState("");
  const [language, setLanguage] = useState<LanguageCode>(preferredLanguage());
  const [selectedId, setSelectedId] = useState("");
  const [selectedDay, setSelectedDay] = useState(defaultPlan.days[0].id);
  const [plan, setPlan] = useState<WorkoutPlan>(readPlan);

  useEffect(() => {
    let cancelled = false;
    loadExerciseData()
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        setSelectedId((current) => current || loaded.exercises[0]?.id || "");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDataError(error instanceof Error ? error.message : "Unable to load exercise data");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allExercises = data?.exercises ?? [];
  const taxonomy = data?.taxonomy;
  const exerciseById = useMemo(() => new Map(allExercises.map((exercise) => [exercise.id, exercise])), [allExercises]);
  const selected = exerciseById.get(selectedId) ?? allExercises[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allExercises.filter((exercise) => {
      const haystack = [
        exercise.name,
        exercise.category,
        exercise.equipment,
        exercise.target,
        exercise.muscleGroup,
        ...exercise.secondaryMuscles
      ]
        .join(" ")
        .toLowerCase();
      return (
        (!q || haystack.includes(q)) &&
        (!category || exercise.category === category) &&
        (!equipment || exercise.equipment === equipment) &&
        (!target || exercise.target === target)
      );
    });
  }, [allExercises, category, equipment, query, target]);

  function updatePlan(nextPlan: WorkoutPlan) {
    setPlan(nextPlan);
    localStorage.setItem(storageKey, JSON.stringify(nextPlan));
  }

  function addToPlan(exercise: Exercise) {
    const item: PlanItem = {
      id: `${exercise.id}-${Date.now()}`,
      exerciseId: exercise.id,
      sets: 3,
      reps: 10,
      weight: "",
      restSeconds: 60,
      notes: ""
    };
    updatePlan({
      ...plan,
      days: plan.days.map((day) => (day.id === selectedDay ? { ...day, items: [...day.items, item] } : day))
    });
  }

  function resetFilters() {
    setQuery("");
    setCategory("");
    setEquipment("");
    setTarget("");
  }

  if (dataError) {
    return (
      <main className="loadingState">
        <Dumbbell size={34} />
        <h1>Workout</h1>
        <p>{dataError}</p>
      </main>
    );
  }

  if (!data || !taxonomy || !selected) {
    return (
      <main className="loadingState">
        <Dumbbell size={34} />
        <h1>Workout</h1>
        <p>Loading exercise library...</p>
      </main>
    );
  }

  return (
    <main className="appShell">
      <section className="libraryPane" aria-label="Exercise library">
        <header className="appHeader">
          <div className="brand">
            <span>W</span>
            <div>
              <strong>Workout</strong>
              <small>{taxonomy.counts.exercises} exercises</small>
            </div>
          </div>
          <a href="/support">Support</a>
        </header>
        <div className="searchBox">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search exercise, muscle, equipment" />
        </div>
        <div className="filters">
          <div>
            <h3>Body part</h3>
            <div className="chipRow">
              {taxonomy.categories.map((item) => (
                <Chip key={item} value={item} selected={category === item} onClick={() => setCategory(category === item ? "" : item)} />
              ))}
            </div>
          </div>
          <div>
            <h3>Equipment</h3>
            <div className="chipRow">
              {taxonomy.equipment.slice(0, 12).map((item) => (
                <Chip key={item} value={item} selected={equipment === item} onClick={() => setEquipment(equipment === item ? "" : item)} />
              ))}
            </div>
          </div>
          <div>
            <h3>Target</h3>
            <div className="chipRow">
              {taxonomy.targets.map((item) => (
                <Chip key={item} value={item} selected={target === item} onClick={() => setTarget(target === item ? "" : item)} />
              ))}
            </div>
          </div>
        </div>
        <div className="resultRow">
          <span>{filtered.length} results</span>
          <button type="button" onClick={resetFilters}>
            <RotateCcw size={14} /> Reset
          </button>
        </div>
        <div className="exerciseGrid">
          {filtered.slice(0, 80).map((exercise) => (
            <ExerciseCard
              exercise={exercise}
              key={exercise.id}
              selected={selected.id === exercise.id}
              onSelect={() => setSelectedId(exercise.id)}
              onAdd={() => {
                setSelectedId(exercise.id);
                addToPlan(exercise);
              }}
            />
          ))}
        </div>
      </section>
      <PlanBuilder plan={plan} selectedDay={selectedDay} onDay={setSelectedDay} onUpdate={updatePlan} exerciseById={exerciseById} />
      <ExerciseDetail
        exercise={selected}
        language={language}
        languages={taxonomy.languages}
        onLanguage={setLanguage}
        onAdd={() => addToPlan(selected)}
      />
      <footer className="disclaimer">
        <Info size={15} /> Workout is an educational reference and planning tool, not medical advice.
      </footer>
    </main>
  );
}
